import axios from "axios";

const state = {
  carousels: [],
  featured_products: [],
  product_categories: [],
  service_categories: [],
  loading: false,
  error: null,
};

const mutations = {
  SET_CAROUSELS(state, carousels) {
    state.carousels = carousels;
  },
  SET_FEATURED_PRODUCTS(state, products) {
    state.featured_products = products;
  },
  SET_PRODUCT_CATEGORIES(state, categories) {
    state.product_categories = categories;
  },
  SET_SERVICE_CATEGORIES(state, services) {
    state.service_categories = services;
  },
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  SET_ERROR(state, error) {
    state.error = error;
  },
  ADD_CAROUSEL(state, carousel) {
    state.carousels.push(carousel);
  },
  UPDATE_CAROUSEL(state, updatedCarousel) {
    const index = state.carousels.findIndex((c) => c.id == updatedCarousel.id);
    if (index !== -1) {
      state.carousels.splice(index, 1, updatedCarousel);
    }
  },
  DELETE_CAROUSEL(state, id) {
    state.carousels = state.carousels.filter((c) => c.id != id);
  },
  ADD_FEATURED_PRODUCT(state, product) {
    state.featured_products.push(product);
  },
  UPDATE_FEATURED_PRODUCT(state, updatedProduct) {
    const index = state.featured_products.findIndex(
      (p) => p.id == updatedProduct.id
    );
    if (index !== -1) {
      state.featured_products.splice(index, 1, updatedProduct);
    }
  },
  DELETE_FEATURED_PRODUCT(state, id) {
    state.featured_products = state.featured_products.filter((p) => p.id != id);
  },
  ADD_PRODUCT_CATEGORY(state, category) {
    state.product_categories.push(category);
  },
  UPDATE_PRODUCT_CATEGORY(state, updatedCategory) {
    const index = state.product_categories.findIndex(
      (c) => c.id == updatedCategory.id
    );
    if (index !== -1) {
      state.product_categories.splice(index, 1, updatedCategory);
    }
  },
  DELETE_PRODUCT_CATEGORY(state, id) {
    state.product_categories = state.product_categories.filter(
      (c) => c.id != id
    );
  },
  ADD_SERVICE_CATEGORY(state, service) {
    state.service_categories.push(service);
  },
  UPDATE_SERVICE_CATEGORY(state, updatedService) {
    const index = state.service_categories.findIndex(
      (s) => s.id == updatedService.id
    );
    if (index !== -1) {
      state.service_categories.splice(index, 1, updatedService);
    }
  },
  DELETE_SERVICE_CATEGORY(state, id) {
    state.service_categories = state.service_categories.filter(
      (s) => s.id != id
    );
  },
};

const actions = {
  // CAROUSELS
  async fetchCarousels({ commit }) {
    commit("SET_LOADING", true);
    try {
      const response = await axios.get("/featured_carousels");
      commit("SET_CAROUSELS", response.data);
      commit("SET_ERROR", null);
    } catch (error) {
      commit(
        "SET_ERROR",
        error.response?.data?.message || "Failed to fetch carousels"
      );
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async createCarousel({ commit }, formData) {
    commit("SET_LOADING", true);
    try {
      const response = await axios.post("/featured_carousel", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      commit("ADD_CAROUSEL", response.data);
      commit("SET_ERROR", null);
      return response.data;
    } catch (error) {
      commit(
        "SET_ERROR",
        error.response?.data?.message || "Failed to create carousel"
      );
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async updateCarousel({ commit }, { id, formData }) {
    commit("SET_LOADING", true);
    try {
      const response = await axios.post(`/featured_carousel/${id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      commit("UPDATE_CAROUSEL", response.data);
      commit("SET_ERROR", null);
      return response.data;
    } catch (error) {
      commit(
        "SET_ERROR",
        error.response?.data?.message || "Failed to update carousel"
      );
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async deleteCarousel({ commit }, id) {
    commit("SET_LOADING", true);
    try {
      await axios.delete(`/featured_carousel/${id}`);
      commit("DELETE_CAROUSEL", id);
      commit("SET_ERROR", null);
    } catch (error) {
      commit(
        "SET_ERROR",
        error.response?.data?.message || "Failed to delete carousel"
      );
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  // FEATURED PRODUCTS
  async fetchFeaturedProducts({ commit }, type = null) {
    commit("SET_LOADING", true);
    try {
      const url = type
        ? `/featured_products?type=${type}`
        : "/featured_products";
      const response = await axios.get(url);
      commit("SET_FEATURED_PRODUCTS", response.data);
      commit("SET_ERROR", null);
    } catch (error) {
      commit(
        "SET_ERROR",
        error.response?.data?.message || "Failed to fetch products"
      );
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async createFeaturedProduct({ commit }, formData) {
    commit("SET_LOADING", true);
    try {
      const response = await axios.post("/featured_product", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      commit("ADD_FEATURED_PRODUCT", response.data);
      commit("SET_ERROR", null);
      return response.data;
    } catch (error) {
      commit(
        "SET_ERROR",
        error.response?.data?.message || "Failed to create product"
      );
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async updateFeaturedProduct({ commit }, { id, formData }) {
    commit("SET_LOADING", true);
    try {
      const response = await axios.post(`/featured_product/${id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      commit("UPDATE_FEATURED_PRODUCT", response.data);
      commit("SET_ERROR", null);
      return response.data;
    } catch (error) {
      commit(
        "SET_ERROR",
        error.response?.data?.message || "Failed to update product"
      );
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async deleteFeaturedProduct({ commit }, id) {
    commit("SET_LOADING", true);
    try {
      await axios.delete(`/featured_product/${id}`);
      commit("DELETE_FEATURED_PRODUCT", id);
      commit("SET_ERROR", null);
    } catch (error) {
      commit(
        "SET_ERROR",
        error.response?.data?.message || "Failed to delete product"
      );
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  // PRODUCT CATEGORIES
  async fetchProductCategories({ commit }) {
    commit("SET_LOADING", true);
    try {
      const response = await axios.get("/product_categories");
      commit("SET_PRODUCT_CATEGORIES", response.data);
      commit("SET_ERROR", null);
    } catch (error) {
      commit(
        "SET_ERROR",
        error.response?.data?.message || "Failed to fetch categories"
      );
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async createProductCategory({ commit }, formData) {
    commit("SET_LOADING", true);
    try {
      const response = await axios.post("/product_category", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      commit("ADD_PRODUCT_CATEGORY", response.data);
      commit("SET_ERROR", null);
      return response.data;
    } catch (error) {
      commit(
        "SET_ERROR",
        error.response?.data?.message || "Failed to create category"
      );
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async updateProductCategory({ commit }, { id, formData }) {
    commit("SET_LOADING", true);
    try {
      const response = await axios.post(`/product_category/${id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      commit("UPDATE_PRODUCT_CATEGORY", response.data);
      commit("SET_ERROR", null);
      return response.data;
    } catch (error) {
      commit(
        "SET_ERROR",
        error.response?.data?.message || "Failed to update category"
      );
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async deleteProductCategory({ commit }, id) {
    commit("SET_LOADING", true);
    try {
      await axios.delete(`/product_category/${id}`);
      commit("DELETE_PRODUCT_CATEGORY", id);
      commit("SET_ERROR", null);
    } catch (error) {
      commit(
        "SET_ERROR",
        error.response?.data?.message || "Failed to delete category"
      );
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  // SERVICE CATEGORIES
  async fetchServiceCategories({ commit }) {
    commit("SET_LOADING", true);
    try {
      const response = await axios.get("/service_categories");
      commit("SET_SERVICE_CATEGORIES", response.data);
      commit("SET_ERROR", null);
    } catch (error) {
      commit(
        "SET_ERROR",
        error.response?.data?.message || "Failed to fetch services"
      );
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async createServiceCategory({ commit }, formData) {
    commit("SET_LOADING", true);
    try {
      const response = await axios.post("/service_category", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      commit("ADD_SERVICE_CATEGORY", response.data);
      commit("SET_ERROR", null);
      return response.data;
    } catch (error) {
      commit(
        "SET_ERROR",
        error.response?.data?.message || "Failed to create service"
      );
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async updateServiceCategory({ commit }, { id, formData }) {
    commit("SET_LOADING", true);
    try {
      const response = await axios.post(`/service_category/${id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      commit("UPDATE_SERVICE_CATEGORY", response.data);
      commit("SET_ERROR", null);
      return response.data;
    } catch (error) {
      commit(
        "SET_ERROR",
        error.response?.data?.message || "Failed to update service"
      );
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async deleteServiceCategory({ commit }, id) {
    commit("SET_LOADING", true);
    try {
      await axios.delete(`/service_category/${id}`);
      commit("DELETE_SERVICE_CATEGORY", id);
      commit("SET_ERROR", null);
    } catch (error) {
      commit(
        "SET_ERROR",
        error.response?.data?.message || "Failed to delete service"
      );
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },
};

const getters = {
  carousels: (state) => state.carousels,
  featuredProducts: (state) => state.featured_products,
  featuredDesktops: (state) =>
    state.featured_products.filter((p) => p.type === "desktop"),
  featuredLaptops: (state) =>
    state.featured_products.filter((p) => p.type === "laptop"),
  productCategories: (state) => state.product_categories,
  serviceCategories: (state) => state.service_categories,
  isLoading: (state) => state.loading,
  error: (state) => state.error,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
